import { base64 } from '@scure/base'
import { rgbaToThumbHash } from 'thumbhash'
import { uploadBlob, mirrorBlob, createUploadAuth } from '@nostr/tools/nipb0'
import { EventTemplate, NostrEvent } from '@nostr/tools'
import { blossomServersAtom } from '@/atoms/lists'
import { signerAtom } from '@/atoms/session'
import { getDefaultStore } from 'jotai'

const jotaiStore = getDefaultStore()

let nextUploadId = 1
export function getNextUploadId(): number {
  return nextUploadId++
}

export interface UploadFile extends File {
  _uploadId: number
}

export type ServerStatus =
  | { state: 'pending' }
  | { state: 'uploading' }
  | { state: 'success' }
  | { state: 'error'; error: string }

export type UploadOptions = {
  signal?: AbortSignal
  cancel?: () => void
  onUploadInit?: (info: { file: UploadFile; cancel: () => void; servers: string[] }) => void
  onServerStatus?: (server: string, status: ServerStatus) => void
}

export const UPLOAD_ABORTED_ERROR_MSG = 'Upload aborted'

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(img.src)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('Failed to decode image'))
    }
    img.src = URL.createObjectURL(file)
  })
}

const MAX_IMAGE_DIM = 2048

async function processImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file

  const img = await loadImage(file)
  let { width, height } = img

  const scale = Math.min(MAX_IMAGE_DIM / width, MAX_IMAGE_DIM / height, 1)
  if (scale < 1) {
    width = Math.round(width * scale)
    height = Math.round(height * scale)
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, width, height)

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(new File([blob], file.name, { type: file.type }))
      } else {
        reject(new Error('Failed to re-encode image'))
      }
    }, file.type)
  })
}

async function generateThumbhash(file: File): Promise<{ hash: Uint8Array; dim: string } | null> {
  if (!file.type.startsWith('image/')) return null

  const img = await loadImage(file)
  const { width, height } = img

  const maxSize = 100
  const scale = Math.min(maxSize / width, maxSize / height, 1)
  const w = Math.round(width * scale)
  const h = Math.round(height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, w, h)

  const rgba = ctx.getImageData(0, 0, w, h).data
  const hash = rgbaToThumbHash(w, h, rgba)
  return { hash, dim: `${width}x${height}` }
}

const imetaTagMap = new Map<string, string[]>()

export async function upload(file: UploadFile, options?: UploadOptions): Promise<{ url: string }> {
  const processed = await processImage(file)
  ;(processed as UploadFile)._uploadId = file._uploadId
  const url = await uploadToBlossom(processed as UploadFile, options)

  const thumb = await generateThumbhash(processed)
  if (thumb) {
    imetaTagMap.set(url, [
      'imeta',
      `url ${url}`,
      `thumbhash ${base64.encode(thumb.hash)}`,
      `dim ${thumb.dim}`
    ])
  }

  return { url }
}

async function uploadToBlossom(file: UploadFile, options?: UploadOptions): Promise<string> {
  const signer = await jotaiStore.get(signerAtom)
  if (!signer) throw new Error('You need to be logged in to upload media')
  if (options?.signal?.aborted) throw new Error(UPLOAD_ABORTED_ERROR_MSG)

  let servers = await jotaiStore.get(blossomServersAtom)
  if (servers.length === 0) servers = window.hallway.universe.defaultBlossomServers

  const cancel = options?.cancel ?? (() => undefined)
  options?.onUploadInit?.({ file, cancel, servers })

  for (const server of servers) options?.onServerStatus?.(server, { state: 'pending' })

  const signEvent = signer.signEvent.bind(signer)
  const { blob, server } = await tryServers(file, servers, signEvent, options)
  mirrorToServers(blob, servers, server, file, signEvent, options)

  return blob.url
}

async function tryServers(
  file: File,
  servers: string[],
  signer: (draft: EventTemplate) => Promise<NostrEvent>,
  options?: UploadOptions
): Promise<{ blob: any; server: string }> {
  const onAuth = async (_server: string, sha256: string) =>
    createUploadAuth(signer, sha256, { type: 'upload' })

  for (const server of servers) {
    options?.onServerStatus?.(server, { state: 'uploading' })
    try {
      const blob = await uploadBlob(server, file, { auth: true, onAuth, signal: options?.signal })
      options?.onServerStatus?.(server, { state: 'success' })
      return { blob, server }
    } catch (e) {
      options?.onServerStatus?.(server, {
        state: 'error',
        error: e instanceof Error ? e.message : String(e)
      })
    }
  }
  throw new Error('All Blossom servers failed to upload')
}

async function mirrorToServers(
  blob: any,
  servers: string[],
  successfulServer: string,
  file: File,
  signer: (draft: any) => Promise<any>,
  options?: UploadOptions
) {
  if (servers.length <= 1) return

  await Promise.allSettled(
    servers
      .filter((s) => s !== successfulServer)
      .map(async (server) => {
        options?.onServerStatus?.(server, { state: 'uploading' })
        try {
          await mirrorBlob(server, blob, {
            auth: true,
            onAuth(_server: string, sha256: string) {
              return createUploadAuth(signer, sha256, { type: 'upload' })
            },
            signal: options?.signal
          })
          options?.onServerStatus?.(server, { state: 'success' })
        } catch {
          try {
            await uploadBlob(server, file, {
              auth: true,
              onAuth(_server: string, sha256: string) {
                return createUploadAuth(signer, sha256, { type: 'upload' })
              },
              signal: options?.signal
            })
            options?.onServerStatus?.(server, { state: 'success' })
          } catch (e) {
            options?.onServerStatus?.(server, {
              state: 'error',
              error: e instanceof Error ? e.message : String(e)
            })
          }
        }
      })
  )
}

export function getImetaTagByUrl(url: string) {
  return imetaTagMap.get(url)
}
